import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { weekDaysDataSource } from 'infrastructure/data-sources/week-days';
import {
  convertDateToTime,
  convertDateToTimeZone,
  convertTime,
  convertTimeToDate,
} from 'infrastructure/functions';
import { Availability } from 'infrastructure/classes/availability';

import s from './styles.module.scss';

import type { InteractiveTimeItem } from 'infrastructure/hooks/interactiveTimeDashboard/types';

interface IProps {
  height: number;
  availabilities: InteractiveTimeItem['contactAvailabilities'];
  timezone: string;
}

const ContactAvailabilityColBody: React.FC<IProps> = (props) => {
  const { height, availabilities, timezone } = props;

  const { t } = useTranslation();

  const weekDays = weekDaysDataSource();
  const [currentPatientDate, setCurrentPatientDate] = useState(
    convertDateToTimeZone(new Date(), timezone),
  );

  const anytime = !availabilities.length;

  const isAvailable = useMemo(() => {
    return Availability.isAvailable(
      availabilities.map(
        (el, i) =>
          new Availability({
            day: el.day,
            startTime: el.startTime,
            endTime: el.endTime,
            guid: `${i}`,
            patientGuid: `${i}`,
          }),
      ),
      timezone,
    );
  }, [availabilities, currentPatientDate, timezone]);

  const availableText = isAvailable
    ? t('labels.available')
    : t('labels.notAvailable');

  const statusText = anytime ? t('labels.anytime') : availableText;

  const timeClassNames = classNames(s.time, {
    [s.green]: isAvailable || anytime,
  });
  const bodyClassNames = classNames(s.body, {
    [s.anytime]: anytime,
  });

  const groupedTimes = useMemo(() => {
    return weekDays
      .map((day, index) => {
        return {
          ...day,
          times: availabilities
            .filter((d) => d.day === index)
            .sort((a, b) => {
              const at = convertTimeToDate(a.startTime).getTime();
              const bt = convertTimeToDate(b.endTime).getTime();
              return at - bt;
            })
            .map(({ startTime, endTime }) => {
              return `${convertTime(startTime)} - ${convertTime(endTime)};`;
            }),
        };
      })
      .filter((el) => el.times.length);
  }, [availabilities, weekDays]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentPatientDate(convertDateToTimeZone(new Date(), timezone));
    }, 60000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <div
      className={bodyClassNames}
      data-cy="interactiveTimeDashboard-contactAvailabilityModal"
    >
      <div
        className={s.header}
        style={{
          height: `${height}px`,
        }}
      >
        <span className={s.title}>{t('labels.contactAvailability')}</span>
        <span className={timeClassNames}>
          {`${convertDateToTime(
            currentPatientDate,
          )} (${timezone}) – ${statusText}`}
        </span>
      </div>
      {!anytime && (
        <ul className={s.list}>
          {groupedTimes.map((el, i) => (
            <li
              key={i}
              style={{
                alignItems: el.times.length > 2 ? 'flex-start' : 'center',
              }}
            >
              <div className={s.day}>
                <span>{el.name.substring(0, 3)}</span>
              </div>
              <div className={s.times}>
                {el.times.length ? (
                  el.times.map((time, e) => (
                    <div key={e} className={s.time}>
                      {time}
                    </div>
                  ))
                ) : (
                  <div className={s.time}>
                    {anytime ? t('labels.anytime') : t('labels.notAvailable')}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ContactAvailabilityColBody;
